import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ControlPasswordProtected from '../components/ControlPasswordProtected';
import {
    Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Box, Typography, Fab, Dialog, DialogTitle, DialogContent, DialogContentText, Alert,
    DialogActions, TextField, Button, TablePagination, List, ListItem, ListItemText, Checkbox
} from '@mui/material';
import { 
    OpenInNew as OpenInNewIcon, 
    Edit as EditIcon, 
    Delete as DeleteIcon, 
    Add as AddIcon,
    ArrowBack as ArrowBackIcon,

} from '@mui/icons-material';
import API_BASE_URL from '../config';
import './Groups.css';

const GroupDetailsContent = () => {
    const { groupId } = useParams();
    const [group, setGroup] = useState(null);
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
  
    const [primaryColor, setPrimaryColor] = useState(localStorage.getItem('primaryColor') || '#007bff');
    const [secondaryColor, setSecondaryColor] = useState(localStorage.getItem('secondaryColor') || '#6c757d');
    const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
    const [textColor, setTextColor] = useState(darkMode ? '#ffffff' : '#000000');
  
    const [openDialog, setOpenDialog] = useState(false);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');
    const [allDevices, setAllDevices] = useState([]);
    const [selectedDevices, setSelectedDevices] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);


  
    const fetchGroupDetails = useCallback(async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/device/group/${groupId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });

        if (response.status === 401) {
            navigate('/unauthorized');
            return;
        }

        if (!response.ok) {
          throw new Error('Failed to fetch group details');
        }
        const data = await response.json();
        setGroup(data);
        setDevices(data.devices || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }, [groupId]);
  
    useEffect(() => {
      fetchGroupDetails();
    }, [fetchGroupDetails]);
  
    useEffect(() => {
      const checkLocalStorage = () => {
        const storedPrimaryColor = localStorage.getItem('primaryColor');
        const storedSecondaryColor = localStorage.getItem('secondaryColor');
        const storedDarkMode = localStorage.getItem('darkMode') === 'true';
  
        if (storedPrimaryColor !== primaryColor) {
          setPrimaryColor(storedPrimaryColor);
        }
        if (storedSecondaryColor !== secondaryColor) {
          setSecondaryColor(storedSecondaryColor);
        }
        if (storedDarkMode !== darkMode) {
          setDarkMode(storedDarkMode);
          setTextColor(storedDarkMode ? '#ffffff' : '#000000');
        }
      };
  
      checkLocalStorage();
      const intervalId = setInterval(checkLocalStorage, 200);
  
      return () => clearInterval(intervalId);
    }, [primaryColor, secondaryColor, darkMode]);
  
    const handlePresetClick = (presetId) => {
      if (presetId) {
        navigate(`/preset/${presetId}`);
      }
    };
  
    const handleChangePage = (event, newPage) => {
      setPage(newPage);
    };
  
    const handleChangeRowsPerPage = (event) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    };
  
    const handleOpenDialog = () => {
      setNewGroupName(group.group_name);
      setOpenDialog(true);
    };
  
    const handleCloseDialog = () => {
      setOpenDialog(false);
    };

    const handleOpenDeleteDialog = () => {
        setOpenDeleteDialog(true);
      };
      
    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
    };

    const handleDeleteGroup = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/device/group/${groupId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
      
          if (!response.ok) {
            throw new Error('Failed to delete group');
          }
      
          handleCloseDeleteDialog();
          navigate('/groups');
        } catch (error) {
          console.error('Error deleting group:', error);
        }
      };

    const fetchAllDevices = useCallback(async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/device/all`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
    
          if (!response.ok) {
            throw new Error('Failed to fetch devices');
          }
    
          const data = await response.json();
          setAllDevices(data);
        } catch (err) {
          console.error('Error fetching devices:', err);
          // Handle error (e.g., show error message to user)
        }
      }, []);
    
    useEffect(() => {
        fetchAllDevices();
    }, [fetchAllDevices]);

    const handleOpenAddDialog = () => {
      setOpenAddDialog(true);
    }

    const handleCloseAddDialog = () => {
        setOpenAddDialog(false);
        setSelectedDevice(null);
    };

    const handleDeviceSelection = (device) => {
        setSelectedDevice(device);
      };
    
      const handleAddDevice = async () => {
        if (!selectedDevice) return;
    
        try {
            const response = await fetch(`${API_BASE_URL}/device/group/${groupId}/devices`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ device_ids: selectedDevice.id })
            });
    
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to add devices to group');
            }
    
            // Refresh the group details to show the newly added devices
            await fetchGroupDetails();
            handleCloseAddDialog();
        } catch (err) {
          console.error('Error adding device to group:', err);
          // Handle error (e.g., show error message to user)
        }
    };
  
    const handleUpdateGroupName = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/device/group/${groupId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ group_name: newGroupName })
        });
  
        if (!response.ok) {
          throw new Error('Failed to update group name');
        }
  
        setGroup({ ...group, group_name: newGroupName });
        handleCloseDialog();
      } catch (error) {
        console.error('Error updating group name:', error);
        // Handle error (e.g., show error message to user)
      }
    };

    const handleScheduleRedirect = () => {
        navigate('/schedules');
    };
    
    const handleAddDevices = async () => {
    // Implement the logic to add selected devices to the group
    };
  
    const handleDeleteDevice = async (deviceId) => {
      try {
        const response = await fetch(`${API_BASE_URL}/device/group/${groupId}/device/${deviceId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
  
        if (!response.ok) {
          throw new Error('Failed to delete device from group');
        }
  
        setDevices(devices.filter(device => device.id !== deviceId));
      } catch (error) {
        console.error('Error deleting device from group:', error);
        // Handle error (e.g., show error message to user)
      }
    };

    const handleBackToGroups = () => {
        navigate('/groups');
    };
  
    if (loading) return <Typography style={{ color: primaryColor }}>Loading...</Typography>;
    if (error) return <Typography style={{ color: primaryColor }}>Error: {error}</Typography>;
    if (!group) return <Typography style={{ color: primaryColor }}>No group found</Typography>;
  
    return (
        <div className={`group-details ${darkMode ? 'dark-mode' : 'light-mode'}`} style={{ height: '100vh', display: 'flex', flexDirection: 'column', padding: '0px', backgroundColor: primaryColor }}>
          <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
            <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToGroups}
            style={{ marginRight: '20px', color: textColor }}
            >
            Back to Groups
            </Button>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
            <Typography variant="h4" style={{ color: textColor, marginRight: '10px' }}>{group.group_name}</Typography>
            <IconButton onClick={handleOpenDialog} style={{ color: textColor }}>
                <EditIcon />
            </IconButton>
            <Button
                variant="outlined"
                startIcon={<DeleteIcon />}
                onClick={handleOpenDeleteDialog}
                sx={{
                marginLeft: '10px',
                color: 'red !important',
                borderColor: 'red !important',
                '&:hover': {
                    backgroundColor: darkMode ? 'rgba(255, 0, 0, 0.1) !important' : 'rgba(255, 0, 0, 0.05) !important',
                    borderColor: 'red !important',
                },
                }}
            >
                Delete Group
            </Button>
          </Box>
          <Typography variant="subtitle1" style={{ color: textColor, marginBottom: '20px' }}>
            Current Preset: {group.preset_id ? (
              <Link
                component="button"
                variant="body2"
                onClick={() => handlePresetClick(group.preset_id)}
                style={{ color: textColor }}
              >
                {group.preset_name}
              </Link>
            ) : 'No Preset'}
            <IconButton onClick={handleScheduleRedirect} style={{ color: textColor }}>
          <OpenInNewIcon />
        </IconButton>
          </Typography>
          
          <Paper sx={{ 
                  flex: 1, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  backgroundColor: secondaryColor,
                  overflow: 'hidden',
                }}>
                  <Box sx={{ 
                    flex: 1, 
                    overflow: 'auto',
                    '& table': {
                      borderCollapse: 'separate',
                      borderSpacing: 0,
                    },
                  }}>
                    <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {['Device Label', 'Type', 'Address', 'Description', 'Actions'].map((header) => (
                      <TableCell 
                        key={header} 
                        sx={{ 
                        backgroundColor: `${darkMode ? '#666666' : '#aaaaaa'} !important`,
                        color: `${textColor} !important`,
                        textAlign: 'center',
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {devices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((device) => (
                    <TableRow key={device.id}>
                        <TableCell align="center" sx={{ color: `${textColor} !important`, padding: '8px' }}>
                            <Link 
                                to={`/device/${device.id}`} 
                                style={{ color: textColor, textDecoration: 'none' }}
                                >
                                {device.label}
                            </Link>
                        </TableCell>
                        <TableCell align="center" sx={{ color: `${textColor} !important`, padding: '8px' }}>
                            {device.type}
                        </TableCell>
                        <TableCell align="center" sx={{ color: `${textColor} !important`, padding: '8px' }}>
                            {device.address}
                        </TableCell>
                        <TableCell align="center" sx={{ color: `${textColor} !important`, padding: '8px' }}>
                            {device.description}
                        </TableCell>
                        <TableCell align="center" sx={{ color: `${textColor} !important`, padding: '8px' }}>
                            <IconButton onClick={() => handleDeleteDevice(device.id)} style={{ color: textColor }}>
                                <DeleteIcon />
                            </IconButton>
                        </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={devices.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              style={{ color: textColor }}
            />
          </Paper>
          <Fab
            variant="extended"
            color="primary"
            aria-label="add group"
            onClick={handleOpenAddDialog}
            sx={{
            position: 'absolute',
            bottom: 72,
            right: 16,
            backgroundColor: primaryColor,
            color: textColor,
            '&:hover': {
                backgroundColor: darkMode ? '#555' : '#e0e0e0',
            },
            }}
        >
            <AddIcon sx={{ mr: 1 }} />
            Add Device
          </Fab>
          <Dialog 
            open={openDialog} 
            onClose={handleCloseDialog}
            PaperProps={{
                style: {
                  backgroundColor: darkMode ? '#333' : '#fff',
                  color: textColor,
                },
            }}
          >
            <DialogTitle>Edit Group Name</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Group Name"
                type="text"
                fullWidth
                value={newGroupName}
                inputProps={{ maxLength: 255 }}
                onChange={(e) => setNewGroupName(e.target.value)}
                sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: darkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: primaryColor,
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: textColor,
                    },
                    '& .MuiInputBase-input': {
                      color: textColor,
                    },
                    '& .MuiFormHelperText-root': {
                      color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                    },
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} sx={{ color: textColor }}>Cancel</Button>
              <Button 
                onClick={handleUpdateGroupName}
                sx={{
                    color: !newGroupName
                      ? (darkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)') 
                      : (darkMode ? '#4caf50' : '#45a049'),
                    backgroundColor: !newGroupName
                      ? 'transparent'
                      : (darkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.1)'), 
                    '&:hover': {
                      backgroundColor: !newGroupName
                        ? 'transparent'
                        : (darkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.2)'),
                    },
                }}
                disabled={!newGroupName}
                >
                Update
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog 
            open={openAddDialog} 
            onClose={handleCloseAddDialog}
            PaperProps={{
            style: {
                backgroundColor: darkMode ? '#333' : '#fff',
                color: textColor,
            },
            }}
        >
            <DialogTitle style={{ color: textColor }}>Add Device to Group</DialogTitle>
            <DialogContent>
            <Alert severity="info" variant="outlined" style={{ marginBottom: '16px', backgroundColor: darkMode ? '#1e3a5f' : '#e6f7ff', color: textColor }}>
                Please note: The new device(s) will change preset with the group when the next schedule for the group comes up.
            </Alert>
                <List>
                    {allDevices.map((device) => (
                        <ListItem 
                            key={device.id} 
                            disabled={devices.some(d => d.id === device.id)}
                            onClick={() => handleDeviceSelection(device)}
                            style={{ cursor: devices.some(d => d.id === device.id) ? 'not-allowed' : 'pointer' }}
                        >
                            <ListItemText primary={device.device_name} />
                            <Checkbox
                            edge="end"
                            checked={selectedDevice && selectedDevice.id === device.id}
                            disabled={devices.some(d => d.id === device.id)}
                            />
                        </ListItem>
                    ))}
                </List>
            </DialogContent>
            <DialogActions>
            <Button onClick={handleCloseAddDialog} style={{ color: textColor }}>Cancel</Button>
            <Button 
                onClick={handleAddDevice} 
                disabled={!selectedDevice || devices.some(d => d.id === selectedDevice.id)}
                sx={{
                    color: !selectedDevice
                      ? (darkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)') 
                      : (darkMode ? '#4caf50' : '#45a049'),
                    backgroundColor: !selectedDevice
                      ? 'transparent'
                      : (darkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.1)'), 
                    '&:hover': {
                      backgroundColor: !selectedDevice
                        ? 'transparent'
                        : (darkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.2)'),
                    },
                }}
            >
                Add
            </Button>
            </DialogActions>
        </Dialog>
        <Dialog 
            open={openDeleteDialog} 
            onClose={handleCloseDeleteDialog}
            PaperProps={{
                style: {
                backgroundColor: darkMode ? '#333' : '#fff',
                color: textColor,
                },
            }}
            >
            <DialogTitle>Delete Group</DialogTitle>
            <DialogContent>
                <DialogContentText style={{ color: textColor }}>
                Are you sure you want to delete this group? This action cannot be undone.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseDeleteDialog} sx={{ color: textColor }}>Cancel</Button>
                <Button 
                onClick={handleDeleteGroup}
                sx={{
                    color: 'red',
                    '&:hover': {
                    backgroundColor: darkMode ? 'rgba(255, 0, 0, 0.1)' : 'rgba(255, 0, 0, 0.05)',
                    },
                }}
                >
                Delete
                </Button>
            </DialogActions>
            </Dialog>
        </div>
    );
};

const GroupDetails = () => {
  const ProtectedComponent = ControlPasswordProtected(GroupDetailsContent);
  return <ProtectedComponent />;
};

export default GroupDetails;